import React, { useEffect, useState } from "react";
import { icons } from "../../../Assets/Icons/icons";
import AddOneButton from "../../common/AddOneButton";
import { createPortal } from "react-dom";
import AddBannerModal from "./AddBannerModal";
import DeleteAlertModal from "../../common/DeleteAlertModal";
import axios from "axios";
import { Pencil, Trash2 } from "lucide-react";

const AdminBannerSkeleton = () => (
  <div className="">
    <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 mt-5 justify-between gap-6">
      {[...Array(6)].map((_, index) => (
        <div key={index} className="w-full group rounded-lg overflow-hidden">
          <div className="w-full h-56 bg-gray-300 rounded-lg animate-pulse"></div>
        </div>
      ))}
    </div>
  </div>
);

const AdminBannerHome = () => {
  const [isAddModal, setIsAddModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null); 
  const [banners, setBanners] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const [deleteModal, setDeleteModal] = useState(false);
  const [dltLoading, setDltLoading] = useState(false);
  const [selectedDelete, setSelectedDelete] = useState(null);

  // Helper function to get English text
  const getEnglishText = (banner, field) => {
    if (!banner || !banner[field]) return "";
    
    // If value is a string (backward compatibility)
    if (typeof banner[field] === 'string') {
      return banner[field];
    }
    
    // If value is an object with language keys
    if (typeof banner[field] === 'object') {
      return banner[field].en || banner[field].english || 
             Object.values(banner[field])[0] || 
             "";
    }
    
    return "";
  };

  // Get English title for display
  const getEnglishTitle = (banner) => {
    const title = getEnglishText(banner, "title");
    return title || "Untitled Banner";
  };

  // Get English subtitle for display (if needed)
  const getEnglishSubtitle = (banner) => {
    return getEnglishText(banner, "subTitle") || "";
  };

  // Fetch all banners
  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/banners/all-banners`
      );
      setBanners(response.data || []);
    } catch (error) {
      setError(error?.response?.data?.message || "Failed to fetch banners");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  // Delete Banner
  const handleDelete = async () => {
    if (!selectedDelete) return;
    try {
      setDltLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/banners/delete-banner/${selectedDelete._id}`
      );
      setDeleteModal(false);
      fetchBanners();
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete banner!");
    } finally {
      setDltLoading(false);
    }
  };

  return (
    <>
      <section>
        <div className="flex flex-col md:flex-row md:items-center space-y-3 md:space-y-0 justify-between">
          <h1 className="text-3xl flex gap-1 items-center capitalize font-philosopher">
            <span className="text-primaryColor">{icons.bannerIcon}</span> All Banners (English View)
          </h1>
          <AddOneButton
            label={"Add Banner"}
            onClick={() => {
              setSelectedBanner(null);
              setIsAddModal(true);
            }}
          />
        </div>

        <div className="min-h-screen mt-5">
          {loading ? (
            <AdminBannerSkeleton />
          ) : error ? (
            <div className="text-center text-red-500">{error}</div>
          ) : banners?.length > 0 ? (
            <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 mt-3 justify-between gap-3">
              {banners.map((banner, index) => {
                const englishTitle = getEnglishTitle(banner);
                const englishSubtitle = getEnglishSubtitle(banner);
                
                return (
                  <figure
                    key={banner._id}
                    className="relative w-full h-56 group rounded-lg overflow-hidden"
                  >
                    <img
                      src={banner.imgUrl}
                      alt={englishTitle}
                      className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500"
                    />

                    {/* Overlay Buttons */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex justify-center items-center gap-3">
                      <button
                        onClick={() => {
                          setSelectedBanner(banner);
                          setIsAddModal(true);
                        }}
                        className="p-2 bg-white/80 rounded-full text-[#6278e8] hover:bg-white hover:scale-110 transition-transform"
                        title="Edit Banner"
                      >
                        <Pencil className="w-4 h-4 text-[#6278e8]" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedDelete(banner);
                          setDeleteModal(true);
                        }}
                        className="p-2 bg-white/80 rounded-full text-red-500 hover:bg-white hover:scale-110 transition-transform"
                        title="Delete Banner"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>

                    <figcaption className="absolute bottom-0 bg-black/70 text-white w-full py-3 px-2">
                      <div className="text-center">
                        <h3 className="text-sm font-medium truncate" title={englishTitle}>
                          {englishTitle}
                        </h3>
                        {englishSubtitle && (
                          <p className="text-xs text-gray-300 truncate mt-1" title={englishSubtitle}>
                            {englishSubtitle.length > 50 ? englishSubtitle.substring(0, 50) + "..." : englishSubtitle}
                          </p>
                        )}
                      </div>
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="text-6xl text-gray-300 mb-4">{icons.folderIcon}</div>
              <h2 className="text-2xl text-gray-500 mb-2">No Banners Found</h2>
              <p className="text-gray-400 mb-6">
                Get started by creating a new banner
              </p>
              <AddOneButton
                onClick={() => setIsAddModal(true)}
                label="Add Banner"
              />
            </div>
          )}
        </div>
      </section>

      {/* Add / Edit Banner Modal */}
      {isAddModal &&
        createPortal(
          <AddBannerModal
            isAddModal={isAddModal}
            onClose={() => setIsAddModal(false)}
            fetchBanners={fetchBanners}
            editData={selectedBanner}
          />,
          document.body
        )}

      {/* Delete Alert Modal */}
      {deleteModal && (
        <DeleteAlertModal
          isOpen={deleteModal}
          onClose={() => setDeleteModal(false)}
          dltType="banner"
          dltName={getEnglishTitle(selectedDelete)}
          onConfirm={handleDelete}
          dltLoading={dltLoading}
        />
      )}
    </>
  );
};

export default AdminBannerHome;





// import React, { useEffect, useState } from "react";
// import { icons } from "../../../Assets/Icons/icons";
// import AddOneButton from "../../common/AddOneButton";
// import { createPortal } from "react-dom";
// import AddBannerModal from "./AddBannerModal";
// import DeleteAlertModal from "../../common/DeleteAlertModal";
// import axios from "axios";
// import { Pencil, Trash2 } from "lucide-react";

// const AdminBannerSkeleton = () => (
//   <div className="">
//     <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 mt-5 justify-between gap-6">
//       {[...Array(6)].map((_, index) => (
//         <div key={index} className="w-full group rounded-lg overflow-hidden">
//           <div className="w-full h-56 bg-gray-300 rounded-lg animate-pulse"></div>
//         </div>
//       ))}
//     </div>
//   </div>
// );

// const AdminBannerHome = () => {
//   const [isAddModal, setIsAddModal] = useState(false);
//   const [selectedBanner, setSelectedBanner] = useState(null); 
//   const [banners, setBanners] = useState([]);
//   const [error, setError] = useState("");
//   const [loading, setLoading] = useState(true);

//   const [deleteModal, setDeleteModal] = useState(false);
//   const [dltLoading, setDltLoading] = useState(false);
//   const [selectedDelete, setSelectedDelete] = useState(null);

//   // Fetch all banners
//   const fetchBanners = async () => {
//     try {
//       setLoading(true);
//       const response = await axios.get(
//         `${process.env.REACT_APP_API_URL}/banners/all-banners`
//       );
//       setBanners(response.data || []);
//     } catch (error) {
//       setError(error?.response?.data?.message || "Failed to fetch banners");
//       console.error(error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchBanners();
//   }, []);

//   // Delete Banner
//   const handleDelete = async () => {
//     if (!selectedDelete) return;
//     try {
//       setDltLoading(true);
//       await axios.delete(
//         `${process.env.REACT_APP_API_URL}/banners/delete-banner/${selectedDelete._id}`
//       );
//       setDeleteModal(false);
//       fetchBanners();
//     } catch (error) {
//       console.error("Delete failed:", error);
//       alert("Failed to delete banner!");
//     } finally {
//       setDltLoading(false);
//     }
//   };

//   return (
//     <>
//       <section>
//         <div className="flex flex-col md:flex-row md:items-center space-y-3 md:space-y-0 justify-between">
//           <h1 className="text-3xl flex gap-1 items-center capitalize font-philosopher">
//             <span className="text-primaryColor">{icons.bannerIcon}</span> All banners
//           </h1>
//           <AddOneButton
//             label={"add banner"}
//             onClick={() => {
//               setSelectedBanner(null);
//               setIsAddModal(true);
//             }}
//           />
//         </div>

//         <div className="min-h-screen mt-5">
//           {loading ? (
//             <AdminBannerSkeleton />
//           ) : error ? (
//             <div className="text-center text-red-500">{error}</div>
//           ) : banners?.length > 0 ? (
//             <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 mt-3 justify-between gap-3">
//               {banners.map((banner, index) => (
//                 <figure
//                   key={index}
//                   className="relative w-full h-56 group rounded-lg overflow-hidden"
//                 >
//                   <img
//                     src={banner.imgUrl}
//                     alt={banner.name}
//                     className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500"
//                   />

//                   {/* Overlay Buttons */}
//                   <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex justify-center items-center gap-3">
//                     <button
//                       onClick={() => {
//                         setSelectedBanner(banner);
//                         setIsAddModal(true);
//                       }}
//                       className="p-2 bg-white/80 rounded-full text-[#6278e8] hover:bg-white hover:scale-110 transition-transform"
//                     >
//                       <Pencil className="w-4 h-4 text-[#6278e8]" />
//                     </button>
//                     <button
//                       onClick={() => {
//                         setSelectedDelete(banner);
//                         setDeleteModal(true);
//                       }}
//                       className="p-2 bg-white/80 rounded-full text-red-500 hover:bg-white hover:scale-110 transition-transform"
//                     >
//                       <Trash2 className="w-4 h-4 text-red-500" />
//                     </button>
//                   </div>

//                   <figcaption className="absolute bottom-0 bg-black/60 text-white w-full py-2 text-center text-sm">
//                     {banner.name}
//                   </figcaption>
//                 </figure>
//               ))}
//             </div>
//           ) : (
//             <div className="flex flex-col items-center justify-center py-16">
//               <div className="text-6xl text-gray-300 mb-4">{icons.folderIcon}</div>
//               <h2 className="text-2xl text-gray-500 mb-2">No Banners Found</h2>
//               <p className="text-gray-400 mb-6">
//                 Get started by creating a new banner
//               </p>
//               <AddOneButton
//                 onClick={() => setIsAddModal(true)}
//                 label="Add Banner"
//               />
//             </div>
//           )}
//         </div>
//       </section>

//       {/* Add / Edit Banner Modal */}
//       {isAddModal &&
//         createPortal(
//           <AddBannerModal
//             isAddModal={isAddModal}
//             onClose={() => setIsAddModal(false)}
//             fetchBanners={fetchBanners}
//             editData={selectedBanner}
//           />,
//           document.body
//         )}

//       {/* Delete Alert Modal */}
//       {deleteModal && (
//         <DeleteAlertModal
//           isOpen={deleteModal}
//           onClose={() => setDeleteModal(false)}
//           dltType="banner"
//           dltName={selectedDelete?.name}
//           onConfirm={handleDelete}
//           dltLoading={dltLoading}
//         />
//       )}
//     </>
//   );
// };

// export default AdminBannerHome;
